import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';
import { baseUnitSchema, stockLocationSchema } from './inventory';

/*
 * Warehouse layout (ADR-0037): a warehouse is a stock location; zones and bins subdivide it.
 * Bin quantities are a sub-projection of the ledger. Branch paths:
 * `/v1/branches/:branchId/warehouse/…`. Temperatures are integer tenths of a degree Celsius.
 */

export const STORAGE_TYPES = [
  'ambient',
  'dry',
  'chiller',
  'freezer',
  'staging',
  'quarantine',
] as const;
export const storageTypeSchema = z.enum(STORAGE_TYPES);
export type StorageType = z.infer<typeof storageTypeSchema>;

const codeSchema = z
  .string()
  .trim()
  .min(1)
  .max(16)
  .regex(/^[A-Z0-9-]+$/, 'Upper-case letters, digits and dash only');
/** −50.0 °C … +100.0 °C in tenths. */
const deciCelsiusSchema = z.number().int().min(-500).max(1000);

export const warehouseZoneSchema = z.object({
  id: uuidSchema,
  location_id: uuidSchema,
  code: z.string(),
  name: z.string(),
  storage_type: storageTypeSchema,
  temperature_min_dc: z.number().int().nullable(),
  temperature_max_dc: z.number().int().nullable(),
  /** Item categories that may be put away here (ADR-0038); null = any. */
  allowed_categories: z.array(z.string()).nullable(),
  active: z.boolean(),
  sort_order: z.number().int(),
  version: z.number().int(),
});
export type WarehouseZone = z.infer<typeof warehouseZoneSchema>;

export const warehouseBinSchema = z.object({
  id: uuidSchema,
  location_id: uuidSchema,
  zone_id: uuidSchema.nullable(),
  code: z.string(),
  barcode: z.string().nullable(),
  /** System bin of unplaced stock (one per bin-managed location; cannot be changed). */
  is_staging: z.boolean(),
  pickable: z.boolean(),
  pick_sequence: z.number().int(),
  active: z.boolean(),
  /** Item × lot buckets with stock in this bin. */
  stock_lines: z.number().int(),
  version: z.number().int(),
});
export type WarehouseBin = z.infer<typeof warehouseBinSchema>;

/** `GET …/warehouse/locations/:locationId/layout`. */
export const warehouseLayoutSchema = z.object({
  location: stockLocationSchema,
  zones: z.array(warehouseZoneSchema),
  bins: z.array(warehouseBinSchema),
});
export type WarehouseLayout = z.infer<typeof warehouseLayoutSchema>;

const temperatureRange = <
  T extends { temperature_min_dc?: number | null; temperature_max_dc?: number | null },
>(
  v: T,
) =>
  v.temperature_min_dc === null ||
  v.temperature_min_dc === undefined ||
  v.temperature_max_dc === null ||
  v.temperature_max_dc === undefined ||
  v.temperature_min_dc <= v.temperature_max_dc;
const temperatureIssue = {
  message: 'The minimum temperature must not exceed the maximum.',
  path: ['temperature_max_dc'],
};

const zoneFields = {
  name: z.string().trim().min(1).max(60),
  storage_type: storageTypeSchema,
  temperature_min_dc: deciCelsiusSchema.nullable(),
  temperature_max_dc: deciCelsiusSchema.nullable(),
  allowed_categories: z.array(z.string().trim().min(1).max(60)).min(1).max(30).nullable(),
  sort_order: z.number().int().min(0),
};
export const createWarehouseZoneRequestSchema = z
  .object({
    location_id: uuidSchema,
    code: codeSchema,
    ...zoneFields,
    temperature_min_dc: zoneFields.temperature_min_dc.default(null),
    temperature_max_dc: zoneFields.temperature_max_dc.default(null),
    allowed_categories: zoneFields.allowed_categories.default(null),
    sort_order: zoneFields.sort_order.default(0),
  })
  .refine(temperatureRange, temperatureIssue);
/** The code is immutable (printed labels, history); no defaults (PATCH). */
export const updateWarehouseZoneRequestSchema = z
  .object({
    expected_version: z.number().int().nonnegative(),
    ...z.object(zoneFields).partial().shape,
    active: z.boolean().optional(),
  })
  .refine(temperatureRange, temperatureIssue);

const binFields = {
  zone_id: uuidSchema,
  barcode: z.string().trim().min(1).max(64).nullable(),
  pickable: z.boolean(),
  pick_sequence: z.number().int().min(0).max(1_000_000),
};
export const createWarehouseBinRequestSchema = z.object({
  location_id: uuidSchema,
  code: codeSchema,
  ...binFields,
  barcode: binFields.barcode.default(null),
  pickable: binFields.pickable.default(true),
  pick_sequence: binFields.pick_sequence.default(0),
});
/** The code is immutable; no defaults (PATCH). */
export const updateWarehouseBinRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  ...z.object(binFields).partial().shape,
  active: z.boolean().optional(),
});

/** `POST …/warehouse/locations/:locationId/enable-bins`: one-way switch, seeds STAGING. */
export const enableBinsRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});

export const binStockQuerySchema = z.object({
  location_id: uuidSchema,
  bin_id: uuidSchema.optional(),
  item_id: uuidSchema.optional(),
});
export const binStockRowSchema = z.object({
  bin_id: uuidSchema,
  bin_code: z.string(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: baseUnitSchema,
  lot_id: uuidSchema.nullable(),
  lot_code: z.string().nullable(),
  expiry_date: z.string().nullable(),
  qty_scaled: z.number().int(),
});
export type BinStockRow = z.infer<typeof binStockRowSchema>;
export const binStockResponseSchema = z.object({ items: z.array(binStockRowSchema) });

// ---- Putaway tasks and relocation (WMS-3, ADR-0038)

const positiveScaled = z
  .number()
  .int()
  .refine((n) => Number.isSafeInteger(n) && n > 0, 'Must be positive');

export const WAREHOUSE_TASK_TYPES = ['putaway', 'pick'] as const;
/** `short`: a pick closed with less than planned (ADR-0039). */
export const WAREHOUSE_TASK_STATUSES = ['open', 'done', 'short', 'cancelled'] as const;
export type WarehouseTaskStatus = (typeof WAREHOUSE_TASK_STATUSES)[number];

/** Why a bin is suggested, best first. */
export const PUTAWAY_SUGGESTION_REASONS = ['same_lot', 'same_item', 'empty', 'other'] as const;
export const putawaySuggestionSchema = z.object({
  bin_id: uuidSchema,
  code: z.string(),
  zone_name: z.string().nullable(),
  reason: z.enum(PUTAWAY_SUGGESTION_REASONS),
});
export type PutawaySuggestion = z.infer<typeof putawaySuggestionSchema>;

export const warehouseTaskSchema = z.object({
  id: uuidSchema,
  task_type: z.enum(WAREHOUSE_TASK_TYPES),
  status: z.enum(WAREHOUSE_TASK_STATUSES),
  location_id: uuidSchema,
  location_name: z.string(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  category: z.string().nullable(),
  base_unit: baseUnitSchema,
  lot_id: uuidSchema.nullable(),
  lot_code: z.string().nullable(),
  expiry_date: z.string().nullable(),
  /** Null for pick tasks at locations without bins. */
  source_bin_id: uuidSchema.nullable(),
  source_bin_code: z.string().nullable(),
  qty_scaled: z.number().int(),
  qty_done_scaled: z.number().int(),
  /** What the source bin holds of this item and lot now (outbound postings may drain it). */
  qty_staged_scaled: z.number().int(),
  source_type: z.string(),
  source_id: z.string(),
  /** Document number when known (goods receipt, requisition). */
  source_reference: z.string().nullable(),
  assignee_id: z.string().nullable(),
  assignee_name: z.string().nullable(),
  priority: z.number().int(),
  due_at: z.string().nullable(),
  created_at: z.string(),
  completed_at: z.string().nullable(),
  cancel_reason: z.string().nullable(),
  /** Open putaway tasks only: up to three destination bins, best first. */
  suggestions: z.array(putawaySuggestionSchema),
  version: z.number().int(),
});
export type WarehouseTask = z.infer<typeof warehouseTaskSchema>;
export const warehouseTaskListResponseSchema = z.object({ items: z.array(warehouseTaskSchema) });

export const warehouseTaskListQuerySchema = z.object({
  status: z.enum(WAREHOUSE_TASK_STATUSES).optional(),
  task_type: z.enum(WAREHOUSE_TASK_TYPES).optional(),
  location_id: uuidSchema.optional(),
  /** `me`: tasks assigned to the caller. */
  assignee: z.literal('me').optional(),
});

/**
 * `POST …/warehouse/tasks/:taskId/confirm`: move stock from the source bin to one or more bins
 * (a split). Partial confirmation leaves the task open with the remainder.
 */
export const confirmPutawayRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  placements: z
    .array(z.object({ bin_id: uuidSchema, qty_scaled: positiveScaled }))
    .min(1)
    .max(20),
});

/**
 * `POST …/warehouse/tasks/:taskId/assign`. Claiming (`assignee_user_id` = caller) or releasing
 * one's own claim needs `warehouse.task`; anything else needs `warehouse.manage`. No defaults:
 * omitted fields stay as they are.
 */
export const assignWarehouseTaskRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  assignee_user_id: z.string().min(1).max(64).nullable().optional(),
  priority: z.number().int().min(0).max(9).optional(),
  due_at: z.iso.datetime().nullable().optional(),
});

/** `POST …/warehouse/tasks/:taskId/cancel` (`warehouse.manage`): stock stays where it is. */
export const cancelWarehouseTaskRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(300),
});

/**
 * `POST …/warehouse/tasks/:taskId/pick` (pick tasks): the quantity actually picked; less than
 * planned closes the task as `short` and releases the rest of its reservation.
 */
export const confirmPickRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  qty_picked_scaled: z.number().int().nonnegative(),
});

/** `POST …/warehouse/relocations`: one bucket from one bin to another of the same location. */
export const relocateStockRequestSchema = z.object({
  location_id: uuidSchema,
  item_id: uuidSchema,
  lot_id: uuidSchema.nullable().default(null),
  from_bin_id: uuidSchema,
  to_bin_id: uuidSchema,
  qty_scaled: positiveScaled,
  reason: z.string().trim().min(1).max(300).nullable().default(null),
});

export const binMovementSchema = z.object({
  id: uuidSchema,
  location_id: uuidSchema,
  item_id: uuidSchema,
  lot_id: uuidSchema.nullable(),
  from_bin_id: uuidSchema.nullable(),
  to_bin_id: uuidSchema.nullable(),
  qty_scaled: z.number().int(),
  source_type: z.string(),
  source_id: z.string(),
  reason: z.string().nullable(),
  moved_at: z.string(),
});
export type BinMovement = z.infer<typeof binMovementSchema>;
export const relocationResponseSchema = z.object({ movements: z.array(binMovementSchema) });
export const confirmPutawayResponseSchema = z.object({
  task: warehouseTaskSchema,
  movements: z.array(binMovementSchema),
});

// ---- Kitchen requisitions (WMS-4, ADR-0039)

export const REQUISITION_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'picking',
  'issued',
  'completed',
  'rejected',
  'cancelled',
] as const;
export type RequisitionStatus = (typeof REQUISITION_STATUSES)[number];

export const requisitionLineSchema = z.object({
  id: uuidSchema,
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: baseUnitSchema,
  qty_requested_scaled: z.number().int(),
  qty_approved_scaled: z.number().int().nullable(),
  qty_reserved_scaled: z.number().int(),
  qty_picked_scaled: z.number().int(),
  qty_issued_scaled: z.number().int(),
  qty_received_scaled: z.number().int().nullable(),
  note: z.string().nullable(),
});
export type RequisitionLine = z.infer<typeof requisitionLineSchema>;

export const requisitionPickSchema = z.object({
  id: uuidSchema,
  line_id: z.string(),
  item_name: z.string(),
  base_unit: baseUnitSchema,
  bin_id: uuidSchema.nullable(),
  bin_code: z.string().nullable(),
  lot_id: uuidSchema.nullable(),
  lot_code: z.string().nullable(),
  expiry_date: z.string().nullable(),
  qty_scaled: z.number().int(),
  qty_done_scaled: z.number().int(),
  status: z.enum(WAREHOUSE_TASK_STATUSES),
  assignee_name: z.string().nullable(),
  version: z.number().int(),
});
export type RequisitionPick = z.infer<typeof requisitionPickSchema>;

export const requisitionSchema = z.object({
  id: uuidSchema,
  requisition_number: z.string(),
  status: z.enum(REQUISITION_STATUSES),
  source_location_id: uuidSchema,
  source_location_name: z.string(),
  destination_location_id: uuidSchema,
  destination_location_name: z.string(),
  needed_at: z.string().nullable(),
  notes: z.string().nullable(),
  currency: z.string(),
  estimated_value_minor: z.number().int().nullable(),
  approval_required: z.boolean().nullable(),
  issued_value_minor: z.number().int().nullable(),
  requested_by: z.string(),
  requested_by_name: z.string().nullable(),
  submitted_at: z.string().nullable(),
  decided_by_name: z.string().nullable(),
  decided_at: z.string().nullable(),
  decision_note: z.string().nullable(),
  issued_by_name: z.string().nullable(),
  issued_at: z.string().nullable(),
  confirmed_by_name: z.string().nullable(),
  confirmed_at: z.string().nullable(),
  confirmation_note: z.string().nullable(),
  cancel_reason: z.string().nullable(),
  created_at: z.string(),
  lines: z.array(requisitionLineSchema),
  /** Pick tasks of the requisition, open first. */
  picks: z.array(requisitionPickSchema),
  version: z.number().int(),
});
export type Requisition = z.infer<typeof requisitionSchema>;
export const requisitionListResponseSchema = z.object({ items: z.array(requisitionSchema) });
export const requisitionListQuerySchema = z.object({
  status: z.enum(REQUISITION_STATUSES).optional(),
  /** `me`: requisitions the caller requested. */
  requested_by: z.literal('me').optional(),
});

const requisitionLineInputSchema = z.object({
  item_id: uuidSchema,
  /** Base unit, scale 1000. */
  qty_scaled: positiveScaled,
  note: z.string().trim().min(1).max(200).nullable().default(null),
});
const requisitionLinesSchema = z
  .array(requisitionLineInputSchema)
  .min(1)
  .max(50)
  .refine((ls) => new Set(ls.map((l) => l.item_id)).size === ls.length, 'One line per item');

/**
 * `POST …/warehouse/requisitions` (`requisition.create`). Source defaults to the branch's default
 * storage location, destination to the consumption (kitchen) location. `submit` creates and
 * submits in one command.
 */
export const createRequisitionRequestSchema = z.object({
  source_location_id: uuidSchema.optional(),
  destination_location_id: uuidSchema.optional(),
  needed_at: z.iso.datetime().nullable().default(null),
  notes: z.string().trim().min(1).max(500).nullable().default(null),
  lines: requisitionLinesSchema,
  submit: z.boolean().default(false),
});
/** Drafts only; `lines` replaces all lines. No defaults (PATCH). */
export const updateRequisitionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  source_location_id: uuidSchema.optional(),
  destination_location_id: uuidSchema.optional(),
  needed_at: z.iso.datetime().nullable().optional(),
  notes: z.string().trim().min(1).max(500).nullable().optional(),
  lines: requisitionLinesSchema.optional(),
});
export const requisitionActionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
});
/**
 * `POST …/:id/approve` (`requisition.approve`): optionally lower line quantities. `note` is
 * required for a self-approval when separation of duties is off.
 */
export const approveRequisitionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  note: z.string().trim().min(1).max(300).nullable().optional(),
  lines: z
    .array(z.object({ line_id: uuidSchema, qty_approved_scaled: z.number().int().nonnegative() }))
    .max(50)
    .optional(),
});
export const rejectRequisitionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  note: z.string().trim().min(3).max(300),
});
export const cancelRequisitionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  reason: z.string().trim().min(3).max(300),
});
/** `POST …/:id/confirm-receipt` (`requisition.create`): what the kitchen actually received. */
export const confirmRequisitionReceiptRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  lines: z
    .array(z.object({ line_id: uuidSchema, qty_received_scaled: z.number().int().nonnegative() }))
    .min(1)
    .max(50),
  note: z.string().trim().min(1).max(300).nullable().optional(),
});

// ---- Receiving quality control (WMS-2)

const checklistItemSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9_]+$/, 'Lower-case letters, digits and underscore only'),
  label: z.string().trim().min(1).max(120),
});

export const inspectionTemplateSchema = z.object({
  id: uuidSchema,
  /** Item category the checklist applies to; null = every category without its own. */
  category: z.string().nullable(),
  name: z.string(),
  /** Receipts with items of this category cannot be posted until each line is inspected. */
  required: z.boolean(),
  checklist: z.array(checklistItemSchema),
  active: z.boolean(),
  version: z.number().int(),
});
export type InspectionTemplate = z.infer<typeof inspectionTemplateSchema>;
export const inspectionTemplateListResponseSchema = z.object({
  items: z.array(inspectionTemplateSchema),
});

const templateFields = {
  name: z.string().trim().min(1).max(80),
  required: z.boolean(),
  checklist: z.array(checklistItemSchema).min(1).max(30),
};
export const createInspectionTemplateRequestSchema = z.object({
  category: z.string().trim().min(1).max(60).nullable().default(null),
  ...templateFields,
  required: templateFields.required.default(false),
});
/** No defaults (PATCH). Changing the checklist bumps the version inspections snapshot. */
export const updateInspectionTemplateRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  ...z.object(templateFields).partial().shape,
  active: z.boolean().optional(),
});

export const qualityInspectionSchema = z.object({
  id: uuidSchema,
  receipt_line_id: uuidSchema,
  template_id: uuidSchema.nullable(),
  template_version: z.number().int().nullable(),
  outcome: z.enum(['passed', 'partial', 'failed']),
  answers: z.array(
    z.object({ key: z.string(), ok: z.boolean(), note: z.string().nullable().optional() }),
  ),
  qty_received_scaled: z.number().int(),
  qty_rejected_scaled: z.number().int(),
  qty_quarantine_scaled: z.number().int(),
  temperature_dc: z.number().int().nullable(),
  notes: z.string().nullable(),
  inspected_by_name: z.string().nullable(),
  inspected_at: z.string(),
});
export type QualityInspection = z.infer<typeof qualityInspectionSchema>;
export const qualityInspectionListResponseSchema = z.object({
  items: z.array(qualityInspectionSchema),
});

// ---- Expected receipts (WMS-2)

/** Open purchase-order lines still to arrive, by expected date (read model, no table). */
export const expectedReceiptSchema = z.object({
  purchase_order_id: uuidSchema,
  po_number: z.string(),
  supplier_id: uuidSchema,
  supplier_name: z.string(),
  expected_date: z.string().nullable(),
  status: z.string(),
  lines: z.array(
    z.object({
      purchase_order_line_id: uuidSchema,
      item_id: uuidSchema,
      item_name: z.string(),
      unit: z.string(),
      qty_ordered_scaled: z.number().int(),
      qty_received_scaled: z.number().int(),
      qty_outstanding_scaled: z.number().int(),
    }),
  ),
});
export type ExpectedReceipt = z.infer<typeof expectedReceiptSchema>;
export const expectedReceiptListResponseSchema = z.object({
  items: z.array(expectedReceiptSchema),
});

// ---- Exceptions (WMS-2)

export const WAREHOUSE_EXCEPTION_TYPES = [
  'qc_quarantine',
  'supplier_rejection',
  'transfer_variance',
  'issue_variance',
  'other',
] as const;
export const WAREHOUSE_EXCEPTION_RESOLUTIONS = [
  'released',
  'disposed',
  'returned',
  'replaced',
  'credited',
  'accepted',
  'other',
] as const;
export const SUPPLIER_RESOLUTION_ACTIONS = [
  'return',
  'replacement',
  'credit_claim',
  'accepted_release',
] as const;

export const warehouseExceptionSchema = z.object({
  id: uuidSchema,
  exception_type: z.enum(WAREHOUSE_EXCEPTION_TYPES),
  reference_type: z.string(),
  reference_id: uuidSchema,
  reference_number: z.string().nullable(),
  reference_line_id: uuidSchema.nullable(),
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: baseUnitSchema,
  lot_id: uuidSchema.nullable(),
  lot_code: z.string().nullable(),
  location_id: uuidSchema.nullable(),
  qty_scaled: z.number().int(),
  reason: z.string(),
  status: z.enum(['open', 'resolved']),
  resolution: z.enum(WAREHOUSE_EXCEPTION_RESOLUTIONS).nullable(),
  resolution_note: z.string().nullable(),
  supplier_action: z.enum(SUPPLIER_RESOLUTION_ACTIONS).nullable(),
  supplier_reference: z.string().nullable(),
  supplier_acknowledgement_note: z.string().nullable(),
  supplier_acknowledged_by_name: z.string().nullable(),
  supplier_acknowledged_at: isoTimestampSchema.nullable(),
  resolution_reference_id: uuidSchema.nullable(),
  created_by_name: z.string().nullable(),
  created_at: z.string(),
  resolved_by_name: z.string().nullable(),
  resolved_at: z.string().nullable(),
  version: z.number().int(),
});
export type WarehouseException = z.infer<typeof warehouseExceptionSchema>;
export const warehouseExceptionListResponseSchema = z.object({
  items: z.array(warehouseExceptionSchema),
});
export const warehouseExceptionListQuerySchema = z.object({
  status: z.enum(['open', 'resolved']).optional(),
  exception_type: z.enum(WAREHOUSE_EXCEPTION_TYPES).optional(),
});
/**
 * Closes an exception without moving stock (`accepted`, `returned` once the supplier took the
 * goods back, `other`). `released` and `disposed` come from their own commands.
 */
export const resolveWarehouseExceptionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  resolution: z.enum(['accepted', 'returned', 'other']),
  note: z.string().trim().min(3).max(300),
});

/** Records the supplier's agreed disposition before warehouse or finance completes it. */
export const acknowledgeSupplierResolutionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  action: z.enum(SUPPLIER_RESOLUTION_ACTIONS),
  supplier_reference: z.string().trim().min(1).max(100),
  note: z.string().trim().min(3).max(300),
});

/** Completes the acknowledged outcome and its physical or financial consequence atomically. */
export const completeSupplierResolutionRequestSchema = z.object({
  expected_version: z.number().int().nonnegative(),
  note: z.string().trim().min(3).max(300),
  to_location_id: uuidSchema.optional(),
  replacement_receipt_id: uuidSchema.optional(),
  credit_note_id: uuidSchema.optional(),
});

// ---- Quarantine (WMS-2)

/** `GET …/warehouse/quarantine`: what sits in the branch's quarantine location. */
export const quarantineStockRowSchema = z.object({
  item_id: uuidSchema,
  sku: z.string(),
  item_name: z.string(),
  base_unit: baseUnitSchema,
  lot_id: uuidSchema.nullable(),
  lot_code: z.string().nullable(),
  expiry_date: z.string().nullable(),
  qty_scaled: z.number().int(),
});
export const quarantineResponseSchema = z.object({
  /** Null until the first quarantined receipt creates it. */
  location_id: uuidSchema.nullable(),
  items: z.array(quarantineStockRowSchema),
});
export type QuarantineResponse = z.infer<typeof quarantineResponseSchema>;

/**
 * `POST …/warehouse/quarantine/release` (`warehouse.release`): moves released stock to a usable
 * location (an intra-branch transfer at unchanged value), optionally resolving an exception.
 */
export const releaseQuarantineRequestSchema = z.object({
  item_id: uuidSchema,
  lot_id: uuidSchema.nullable().default(null),
  qty_scaled: z
    .number()
    .int()
    .refine((n) => Number.isSafeInteger(n) && n > 0, 'Must be positive'),
  to_location_id: uuidSchema,
  reason: z.string().trim().min(3).max(300),
  exception_id: uuidSchema.nullable().default(null),
});
